import { useState } from 'react'
import { Avatar, Box, Typography } from '@mui/material'
import AdxAutocomplete from 'components/reusable/AdxAutocomplete'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import defaultImage from 'assets/images/default_avatar.png'

const StaffSelector = ({ label, value, options, onChange, error }) => {
  const [isInteracting, setIsInteracting] = useState(false)

  return (
    <AdxAutocomplete
      label={isInteracting ? null : label}
      placeholder="Select an active staff member"
      options={options}
      value={value || null}
      onOpen={() => setIsInteracting(true)}
      onFocus={() => setIsInteracting(true)}
      onBlur={() => setIsInteracting(false)}
      onChange={(_, selected) => onChange(selected)}
      getOptionLabel={(option) => option?.name || ''}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      renderOption={(props, option) => (
        <Box component="li" {...props} sx={{ display: 'flex', gap: 1.5 }}>
          <Avatar src={option.profile_image_url || defaultImage} sx={{ width: 36, height: 36 }} />
          <Box>
            <Typography variant="body1">{option.name}</Typography>
            <Typography variant="caption" color="light-gray-text.main">
              {[option.designation, option.department].filter(Boolean).join(', ')}
            </Typography>
          </Box>
        </Box>
      )}
      isErrorShown={Boolean(error)}
      errorMessage={<AdxErrorMessage>{error}</AdxErrorMessage>}
    />
  )
}

const TerminationReplacementForm = ({
  impact,
  staffOptions,
  replacements,
  errors,
  onReportingManagerChange,
  onDepartmentHeadChange,
  onApprovalRoleChange,
}) => {
  const directReports = impact?.direct_reports || []
  const departments = impact?.departments_headed || []
  const approvalRoles = impact?.sole_approval_groups || []

  return (
    <Box display="flex" flexDirection="column" gap={2.5}>
      <Box>
        <Typography variant="body1" color="dark-text.main" fontWeight="bold">
          Assign responsibilities before termination
        </Typography>
        <Typography variant="body2" color="light-gray-text.main" mt={0.5}>
          The selected replacements take effect when termination is finalized. Pending leave will be
          withdrawn or rejected automatically and is not transferred.
        </Typography>
      </Box>

      {directReports.length > 0 && (
        <Box display="flex" flexDirection="column" gap={1}>
          <Typography variant="body2" color="light-gray-text.main">
            {directReports.length} active{' '}
            {directReports.length === 1 ? 'staff member reports' : 'staff members report'} to this
            employee.
          </Typography>
          <Typography variant="caption" color="light-gray-text.main">
            {directReports.map((directReport) => directReport.name).join(', ')}
          </Typography>
          <StaffSelector
            label={`New reporting manager for ${directReports.length} ${
              directReports.length === 1 ? 'staff member' : 'staff members'
            }`}
            value={replacements.reportingManager}
            options={staffOptions}
            onChange={onReportingManagerChange}
            error={errors.reportingManager}
          />
        </Box>
      )}

      {departments.map((department) => (
        <StaffSelector
          key={`department-${department.id}`}
          label={`New head for ${department.name}`}
          value={replacements.departmentHeads[department.id]}
          options={staffOptions}
          onChange={(selected) => onDepartmentHeadChange(department.id, selected)}
          error={errors.departmentHeads?.[department.id]}
        />
      ))}

      {approvalRoles.map((role) => (
        <StaffSelector
          key={`approval-role-${role.id}`}
          label={`New active member for ${role.name}`}
          value={replacements.approvalRoles[role.id]}
          options={staffOptions}
          onChange={(selected) => onApprovalRoleChange(role.id, selected)}
          error={errors.approvalRoles?.[role.id]}
        />
      ))}
    </Box>
  )
}

export default TerminationReplacementForm
